import { BridgeError, HttpBridge, HttpEventsChannel } from '@kinoq/camera-protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type BridgeProbe, probeBridge } from './bridgeProbe';
import type { CameraSource } from './cameraSource';
import { bridgeUrl } from './desktopDiscovery';

interface BridgeCameraSource extends CameraSource {
  /** Object URL of the latest preview frame. */
  frameUrl: string | undefined;
  /** `<bridge name> · <lens label>` once a lens is bound; empty before. */
  caption: string;
}

interface Session {
  bridge: HttpBridge;
  /** Captures run one at a time: a bridge serves one still at a time. */
  captures: Promise<unknown>;
  /** Ends the preview, so the loop re-reads `/bridge` and binds again. */
  end: () => void;
}

const RETRY_MS = 5000;
const FOLLOW_MS = 500;
const NOT_ANSWERING = 'not answering · retrying';
const WAITING = 'waiting for the bridge';

const STATUS: Record<Exclude<BridgeProbe['kind'], 'live'>, string> = {
  unknown: 'not found on the network',
  old: 'needs a newer bridge',
  unreachable: NOT_ANSWERING,
};

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done);
  });
}

/**
 * One probe, bind and preview; resolves with the status to show once it ends. `following` is
 * the lens of a live session that just ended: while `/bridge` reads null or that lens, the
 * bridge may be mid-rebind for another device, and binding the first usable camera or the old
 * lens would undo that device's choice.
 */
async function attempt(
  id: string,
  signal: AbortSignal,
  following: string | undefined,
  on: { bound(bridge: HttpBridge, caption: string, lens: string): void; frame(data: Blob): void },
): Promise<string> {
  const probe = await probeBridge(id, signal);
  if (probe.kind !== 'live') return STATUS[probe.kind];
  const { bound } = probe.info;
  if (following && (bound === null || bound === following)) return WAITING;
  try {
    const bridge = new HttpBridge(bridgeUrl(id));
    const cameras = await bridge.listCameras();
    const lens = cameras.find((c) => c.id === bound) ?? cameras.find((c) => !c.unusable);
    if (!lens) return 'no usable camera';
    await bridge.connect(lens.id);
    if (signal.aborted) return '';
    on.bound(bridge, `${probe.info.name} · ${lens.label}`, lens.id);
    for await (const frame of bridge.startPreview(signal)) on.frame(frame.data);
  } catch (e) {
    if (e instanceof BridgeError && e.message.includes('too many viewers')) {
      return 'too many viewers';
    }
  }
  return NOT_ANSWERING;
}

/**
 * The lens a KinoQ bridge has bound, or its first usable camera when none is: previews it,
 * treats the bridge's trigger (a shutter pressed on the phone) as `onTrigger`, and while
 * not live retries every 5 s. Other devices share the lens, so closing never disconnects.
 */
export function useBridgeCamera(
  bridgeId: string | null,
  onTrigger: () => void,
): BridgeCameraSource {
  const [frameUrl, setFrameUrl] = useState<string>();
  const [error, setError] = useState('');
  const [caption, setCaption] = useState('');
  const session = useRef<Session | null>(null);
  const triggerRef = useRef(onTrigger);
  useEffect(() => {
    triggerRef.current = onTrigger;
  });

  useEffect(() => {
    if (!bridgeId) return;
    const closed = new AbortController();
    let url: string | undefined;
    const show = (next: string | undefined) => {
      if (url) URL.revokeObjectURL(url);
      url = next;
      setFrameUrl(next);
    };
    void (async () => {
      let following: string | undefined;
      let waits = 0;
      let lastQuickRetry = Number.NEGATIVE_INFINITY;
      while (!closed.signal.aborted) {
        const ended = new AbortController();
        let unsubscribe = () => {};
        let lens: string | undefined;
        let live = false;
        const signal = AbortSignal.any([closed.signal, ended.signal]);
        const status = await attempt(bridgeId, signal, following, {
          bound: (bridge, text, id) => {
            session.current = { bridge, captures: Promise.resolve(), end: () => ended.abort() };
            setCaption(text);
            lens = id;
          },
          frame: (data) => {
            if (!live) {
              setError('');
              // Not before: a listener disables the station's trigger for everyone else.
              unsubscribe = new HttpEventsChannel(bridgeUrl(bridgeId)).subscribeEvents(
                (event) => {
                  if (event.type === 'trigger') triggerRef.current();
                },
                () => {},
              );
            }
            live = true;
            show(URL.createObjectURL(data));
          },
        });
        unsubscribe();
        session.current = null;
        show(undefined);
        if (closed.signal.aborted) return;
        if (live) {
          following = lens;
          waits = 0;
          // Most often a lens bound elsewhere: re-read at once, but only once per 5 s, so a
          // bridge that fails right after a frame is not hammered.
          if (Date.now() - lastQuickRetry >= RETRY_MS) {
            lastQuickRetry = Date.now();
            continue;
          }
        } else if (status === WAITING) {
          // Past this a bridge reporting nothing (or the same lens) is settled, not rebinding.
          if (++waits * FOLLOW_MS >= RETRY_MS) following = undefined;
          else await pause(FOLLOW_MS, closed.signal);
          continue;
        }
        setError(status);
        await pause(RETRY_MS, closed.signal);
      }
    })();
    return () => closed.abort();
  }, [bridgeId]);

  const capture = useCallback(async () => {
    const current = session.current;
    if (!current) throw new Error('The camera is not connected yet.');
    const shot = current.captures.then(() => current.bridge.capture());
    current.captures = shot.catch(() => {});
    try {
      return (await shot).blob;
    } catch (e) {
      const code = e instanceof BridgeError ? e.code : undefined;
      if (code === 'NotConnected') current.end();
      throw new Error(
        code === 'CaptureFailed'
          ? 'The camera could not take the photo.'
          : 'The camera is not reachable.',
      );
    }
  }, []);

  return { ready: frameUrl !== undefined, error, frameUrl, capture, caption };
}
